import { useState, useEffect } from 'react';
import Sentry from '@/lib/sentry-safe';
import { storageApi } from '@/lib/supabase';
import { normalizeAudioSourceUrl } from '@/lib/audio-url-normalization';

const isDev = process.env.NODE_ENV === 'development';

// LRU Cache with TTL for audio URLs (prevents memory leaks)
class LRUCache<K, V> {
  private cache = new Map<K, { value: V; timestamp: number }>();

  constructor(private maxSize: number, private ttlMs: number) {}

  get(key: K): V | undefined {
    const entry = this.cache.get(key);
    if (!entry) return undefined;

    // Check if entry has expired
    if (Date.now() - entry.timestamp > this.ttlMs) {
      this.cache.delete(key);
      return undefined;
    }

    // Move to end (most recently used) by re-inserting
    this.cache.delete(key);
    this.cache.set(key, entry);
    return entry.value;
  }

  set(key: K, value: V): void {
    // Remove oldest entry if at capacity
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey !== undefined) {
        this.cache.delete(firstKey);
      }
    }
    this.cache.set(key, { value, timestamp: Date.now() });
  }

  has(key: K): boolean {
    return this.get(key) !== undefined;
  }
}

// Cache with max 50 entries and 30-minute TTL
const audioUrlCache = new LRUCache<string, string>(50, 30 * 60 * 1000);

// Exported for testing only — reset cache state between tests
export const _resetAudioUrlCacheForTesting = () => {
  (audioUrlCache as unknown as { cache: Map<unknown, unknown> }).cache.clear();
};

// Fallback demo audio URL - uses existing sound from Supabase storage
// Using Nokia Tone as a neutral, short fallback sound
const FALLBACK_DEMO_AUDIO = 'https://qghjtvlcywfcrntysqkl.supabase.co/storage/v1/object/public/audio-files/sports/nokia_tone_sound.wav';

// Synchronous resolution that needs no network call. Either yields a final
// URL (`url`) or signals that an async storage lookup is required (`fetchKey`).
// `seedKey` carries a server-resolved URL that should be warmed into the LRU
// cache (done in an effect, never during render).
type SyncResolution = {
  // Final URL when resolvable without a network call (omitted when a fetch
  // is required — `fetchKey` is set instead).
  url?: string | null;
  // Normalized key to fetch via storageApi when set.
  fetchKey?: string;
  // Server-resolved URL key to warm into the LRU cache (effect-only).
  seedKey?: string | null;
};

function resolveAudioUrlSync(
  fileUrl: string | null,
  initialUrl?: string | null
): SyncResolution {
  if (!fileUrl) return { url: null };

  // Server already resolved this URL (TES-831) — use it immediately and warm
  // the cache so later consumers without `initialUrl` are also instant.
  if (initialUrl) {
    return { url: initialUrl, seedKey: normalizeAudioSourceUrl(fileUrl) };
  }

  if (typeof fileUrl !== 'string' || fileUrl.trim() === '') {
    return { url: FALLBACK_DEMO_AUDIO };
  }

  const normalizedFileUrl = normalizeAudioSourceUrl(fileUrl);
  if (!normalizedFileUrl) return { url: FALLBACK_DEMO_AUDIO };

  // LRU read during render is a benign, idempotent recency bump — the value
  // returned is stable, so this stays render-pure for our purposes.
  const cachedUrl = audioUrlCache.get(normalizedFileUrl);
  if (cachedUrl) return { url: cachedUrl };

  return { fetchKey: normalizedFileUrl };
}

export const useAudioUrl = (fileUrl: string | null, initialUrl?: string | null) => {
  // Everything resolvable without a network call is derived during render,
  // so no synchronous setState-in-effect is needed (react-hooks/set-state-in-effect).
  const sync = resolveAudioUrlSync(fileUrl, initialUrl);
  const fetchKey = sync.fetchKey;

  // Holds only the asynchronously fetched result, keyed by the normalized URL
  // it belongs to so a stale fetch can't leak into a newer fileUrl.
  const [fetched, setFetched] = useState<{ key: string; url: string } | null>(null);
  // Preserved for API compatibility; the original hook never surfaced a
  // non-null error (failures fall back to demo audio), so neither does this.
  const [error] = useState<string | null>(null);

  const fetchedMatches = fetchKey != null && fetched?.key === fetchKey;
  const audioUrl = fetchKey == null ? sync.url ?? null : fetchedMatches ? fetched!.url : null;
  const isLoading = fetchKey != null && !fetchedMatches;

  // Warm the LRU cache with a server-resolved URL. Side effect only (no
  // setState) so it never triggers a cascading render.
  useEffect(() => {
    if (sync.seedKey && !audioUrlCache.has(sync.seedKey) && initialUrl) {
      audioUrlCache.set(sync.seedKey, initialUrl);
    }
  }, [sync.seedKey, initialUrl]);

  // The only async work. setState happens strictly after `await`, so it is a
  // subscription-style callback — not a synchronous effect body update.
  useEffect(() => {
    if (fetchKey == null) return;
    let cancelled = false;

    (async () => {
      try {
        if (isDev) console.log(`[useAudioUrl] Fetching URL for: ${fetchKey}`);
        const url = await storageApi.getAudioUrl(fetchKey);

        let resolved: string;
        if (url) {
          try {
            if (!url.startsWith('/')) new URL(url); // validate absolute URLs
            resolved = url;
            if (isDev) console.log(`[useAudioUrl] URL fetched and cached: ${url}`);
          } catch {
            if (isDev) console.log('[useAudioUrl] Invalid URL, using fallback');
            resolved = FALLBACK_DEMO_AUDIO;
          }
        } else {
          if (isDev) console.log('[useAudioUrl] No URL returned, using fallback');
          resolved = FALLBACK_DEMO_AUDIO;
        }

        audioUrlCache.set(fetchKey, resolved);
        if (!cancelled) setFetched({ key: fetchKey, url: resolved });
      } catch (err) {
        // SECURITY: Don't include file URLs in Sentry logs
        Sentry.captureException(err, { tags: { context: 'useAudioUrl', action: 'getAudioUrl' } });
        if (isDev) console.log('[useAudioUrl] Error, using fallback');
        audioUrlCache.set(fetchKey, FALLBACK_DEMO_AUDIO);
        if (!cancelled) setFetched({ key: fetchKey, url: FALLBACK_DEMO_AUDIO });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchKey]);

  return { audioUrl, isLoading, error };
};
